// RiskExplainPivotTable.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DateBox } from "devextreme-react/date-box";
import PivotGrid, { FieldChooser, FieldPanel } from "devextreme-react/pivot-grid";
import PivotGridDataSource from "devextreme/ui/pivot_grid/data_source";
import "./PositionsPositions.css";
import { HeaderFilter } from "devextreme-react/data-grid";
import { Search } from "devextreme-react/gantt";
import { SelectBox } from "devextreme-react/select-box";

import saveAs from "file-saver";
import * as ExcelJS from "exceljs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { exportPivotGrid } from "devextreme/excel_exporter";
import { baseUrl } from "../../hooks/useFetch";
import useCobDate from "../../hooks/useCobDate";
import { toDate, fmt } from "../../utils/dateUtils";

interface Measure {
  id: number;
  name: string;
}

interface BucketItem {
  id: string;
  name: string;
}

interface FlatRow {
  node: any;
  level: number;
}

interface ColLeaf {
  node: any;
  path: string[];
}

const RiskExplainPivotTable: React.FC = () => {
  const pivotRef = useRef<any>(null);

  const [cobDate, setCobDate, cobLoading] = useCobDate("/risk_explain_latest_cob");
  const [, , , latestCobDate] = useCobDate("/market_risk/sp_matrix_latest_cob");
  const [positionData, setPositionData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [searchEnabled, setSearchEnabled] = useState<boolean>(true);
  const [showRelevantValues, setShowRelevantValues] = useState<boolean>(true);

  const MEASURES = useMemo<Measure[]>(
    () => [
      { id: 95, name: "1 Day VaR (95%)" },
      { id: 99, name: "1 Day VaR (99%)" },
    ],
    []
  );

  const [measureId, setMeasureId] = useState<number>(99);
  const [elementName, setElementName] = useState<string>("RMS Total");
  const [elements, setElements] = useState<BucketItem[]>([]);

  // stable key parts to avoid "same values but new Date() object"
  const cobDateStr = useMemo(
    () => fmt(cobDate),
    [cobDate ? cobDate.getTime() : null]
  );

  // 2) Fetch risk_explain whenever cob_date OR measure OR bucket changes
  //    Hard guard to prevent repeat fetches if parent re-renders / StrictMode / etc.
  const lastFetchKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        const r = await fetch(
          `${baseUrl}/risk_explain_buckets?cob_date=${encodeURIComponent(cobDateStr ?? "")}`,
          { signal: ac.signal }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();

        const arr: any[] = Array.isArray(j)
          ? j
          : j?.data ?? j?.rows ?? j?.buckets ?? [];
        const items: BucketItem[] = arr.map((x: any) =>
          typeof x === "string"
            ? { id: x, name: x }
            : { id: x.id ?? x.name, name: x.name ?? x.id }
        );

        setElements(items);

        // default selection (only if none picked yet)
        setElementName((prev) => prev ?? items?.[0]?.id ?? null as any);
      } catch (e: any) {
        if (e?.name !== "AbortError") console.error(e);
      }
    })();

    return () => ac.abort();
  }, [baseUrl, cobDate]);

  useEffect(() => {
    if (!cobDateStr) return;

    const fetchKey = `${cobDateStr}__${measureId}__${elementName}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;

    const ac = new AbortController();
    setLoading(true);

    (async () => {
      try {
        const url = `${baseUrl}/risk_explain?cob_date=${encodeURIComponent(
          cobDateStr
        )}&measure=${encodeURIComponent(measureId)}&bucket=${encodeURIComponent(elementName)}`;

        const r = await fetch(url, { signal: ac.signal });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const { data } = await r.json();

        setPositionData(data || []);
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          console.error(e);
          // allow retry if something failed
          lastFetchKeyRef.current = null;
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      ac.abort();
    };
  }, [cobDateStr, measureId, elementName]);

  const fields = useMemo(
    () => [
      { caption: "Asset Type", dataField: "asset_type", area: "row" },
      { caption: "Asset Class", dataField: "asset_class", area: "row" },
      { caption: "Underlying", dataField: "underlying", area: "row" },

      { caption: "Year", dataField: "year", area: "column" },
      { caption: "Quarter", dataField: "quarter", area: "column" },
      { caption: "Month", dataField: "month", area: "column" },

      {
        caption: "Component Var",
        dataField: "component_var",
        area: "data",
        summaryType: "sum",
        format: "fixedPoint",
      },
    ],
    []
  );

  const dataSource = useMemo(() => {
    const mappedFields: any[] = fields.map((f) => {
      if (f.dataField === "component_var") {
        return {
          ...f,
          dataType: "number" as const,
          format: { type: "fixedPoint", precision: 0 },
          customizeText: (cellInfo: any) => {
            const { value } = cellInfo;
            const formatted = cellInfo.text ?? cellInfo.valueText ?? "";
            if (!Number.isFinite(value)) return formatted;
            if (value < 0) {
              const absFmt = Math.abs(value).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              });
              return `(${absFmt})`;
            }
            return formatted;
          },
        };
      }
      return f;
    });
    return new PivotGridDataSource({
      fields: mappedFields,
      store: positionData || [],
    });
  }, [fields, positionData]);

  const onCellPrepared = (e: any): void => {
    const v = e.cell?.value;
    e.cellElement.classList.toggle("neg", Number.isFinite(v) && v < 0);
  };

  const onExport = async (): Promise<void> => {
    if (!pivotRef.current) return;
    const pivotInstance = pivotRef.current.instance();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Pivot");

    await exportPivotGrid({
      component: pivotInstance,
      worksheet,
      customizeCell: ({ pivotCell, excelCell }: any) => {
        if (!pivotCell) return;

        if (
          pivotCell.area === "row" ||
          pivotCell.area === "column" ||
          pivotCell.area === "filter" ||
          (pivotCell.area === "data" &&
            pivotCell.rowType === "D" &&
            pivotCell.columnType === "D" &&
            pivotCell.type === "D")
        ) {
          excelCell.font = { ...(excelCell.font || {}), bold: true };
        }

        if (pivotCell.area === "data" && pivotCell.value != null) {
          const numericValue = Number(pivotCell.value);
          if (!Number.isNaN(numericValue)) {
            excelCell.value = numericValue;
            excelCell.numFmt = "#,##0.00;[Red]-#,##0.00";
            if (numericValue < 0) {
              excelCell.font = {
                ...(excelCell.font || {}),
                color: { argb: "FFFF0000" },
              };
            }
          }
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "risk_explain.xlsx");
  };

  const onExportPdf = async (): Promise<void> => {
    const pivot = pivotRef.current.instance();
    const ds = pivot.getDataSource();
    await ds.load();

    const data = ds.getData();
    const rows = data.rows || [];
    const cols = data.columns || [];
    const values = data.values || [];

    const flattenRows = (
      nodes: any[],
      level: number = 0,
      out: FlatRow[] = []
    ): FlatRow[] => {
      (nodes || []).forEach((n: any) => {
        out.push({ node: n, level });
        if (n.children?.length) flattenRows(n.children, level + 1, out);
      });
      return out;
    };

    const leafColumnPaths = (
      nodes: any[],
      path: string[] = [],
      out: ColLeaf[] = []
    ): ColLeaf[] => {
      (nodes || []).forEach((n: any) => {
        const next = [...path, (n.text ?? n.value ?? "")].filter(Boolean);
        if (n.children?.length) leafColumnPaths(n.children, next, out);
        else out.push({ node: n, path: next });
      });
      return out;
    };

    const cellValue = (vals: any, r: number, c: number): string | number => {
      const cellArr = vals?.[r]?.[c];
      const raw = Array.isArray(cellArr) ? cellArr[0] : cellArr;
      const v =
        raw && typeof raw === "object" && "value" in raw ? raw.value : raw;
      if (v == null) return "";
      const num = Number(v);
      return Number.isNaN(num) ? v : num;
    };

    const rowsFlat = flattenRows(rows);
    const colLeaves = leafColumnPaths(cols);

    const rowFields = ds.getAreaFields("row");
    const headRowLabel = rowFields?.[0]?.caption ?? "Row";

    const head = [
      [
        headRowLabel,
        ...(colLeaves.length
          ? colLeaves.map((c) => c.path.join(" / "))
          : ["Value"]),
      ],
    ];

    const body = rowsFlat.map((r, rowIndex) => {
      const rowLabel = `${"  ".repeat(r.level)}${r.node.text ?? r.node.value ?? ""}`;
      if (!colLeaves.length) return [rowLabel, cellValue(values, rowIndex, 0)];
      return [
        rowLabel,
        ...colLeaves.map((_: ColLeaf, colIndex: number) =>
          cellValue(values, rowIndex, colIndex)
        ),
      ];
    });

    const doc = new jsPDF("landscape", "pt", "a4");
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(
      `Risk Explain ${cobDateStr ?? ""} | ${measureId} | ${elementName}`,
      20,
      25
    );

    (doc as any).autoTable({
      head,
      body,
      styles: { fontSize: 9 },
      headStyles: {
        fontStyle: "bold",
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
      },
      didParseCell: ({ section, cell, column }: any) => {
        if (
          section === "body" &&
          column.index > 0 &&
          typeof cell.raw === "number" &&
          cell.raw < 0
        ) {
          cell.styles.textColor = [255, 0, 0];
          cell.styles.fontStyle = "bold";
        }
      },
      margin: { top: 40, left: 20, right: 20, bottom: 20 },
    });

    doc.save("risk_explain.pdf");
  };

  return (
    <div className="dashboard-container">
      <div className="filters-row">
        <div className="filter-block">
          <div className="filters-group">
            <label>CoB:</label>
            <DateBox
              displayFormat="dd/MM/yyyy"
              inputAttr={{ style: "font-size:12px; line-height:20px;" }}
              useMaskBehavior
              pickerType="calendar"
              value={cobDate}
              onValueChanged={(e: any) => setCobDate(toDate(e.value))}
              width={150}
              max={latestCobDate ?? undefined}
            />
          </div>

          <div className="filters-group">
            <label>Measure:</label>
            <SelectBox
              inputAttr={{ style: "font-size:12px; line-height:20px;" }}
              dropDownOptions={{ wrapperAttr: { class: "sb-small-items" } }}
              dataSource={MEASURES}
              valueExpr="id"
              displayExpr="name"
              value={measureId}
              width={180}
              onValueChanged={(e: any) => setMeasureId(e.value)}
            />
          </div>

          <div className="filters-group">
            <label>Bucket:</label>
            <SelectBox
              inputAttr={{ style: "font-size:12px; line-height:20px;" }}
              dropDownOptions={{ wrapperAttr: { class: "sb-small-items" } }}
              dataSource={elements}
              valueExpr="id"
              displayExpr="name"
              value={elementName}
              width={180}
              onValueChanged={(e: any) => setElementName(e.value)}
            />
          </div>
        </div>
      </div>

      <div className="positions-pivot-section">
        <div className="positions-pivot-scroll">
          <PivotGrid
            ref={pivotRef}
            id="pivotgrid"
            dataSource={dataSource}
            allowFiltering
            allowSorting
            allowSortingBySummary
            showBorders
            // @ts-ignore
            showDataFields
            dataFieldArea="row"
            showRowFields
            showColumnFields
            height="70vh"
            width="100%"
            onCellPrepared={onCellPrepared}
          >
            <HeaderFilter
              {...{ showRelevantValues } as any}
              width={300}
              height={400}
            >
              <Search enabled={searchEnabled} />
            </HeaderFilter>
            <FieldChooser allowSearch />
            <FieldPanel visible />
          </PivotGrid>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          marginTop: "20px",
        }}
      >
        <button
          onClick={onExport}
          style={{
            padding: "10px 18px",
            borderRadius: "4px",
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          Export to Excel
        </button>
        <button
          onClick={onExportPdf}
          style={{
            padding: "10px 18px",
            borderRadius: "4px",
            backgroundColor: "#d9534f",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          Export to PDF
        </button>
      </div>
    </div>
  );
};

export default RiskExplainPivotTable;
